/**
 * Logo upload utilities for company branding
 */

/**
 * Convert an image file to base64
 * @param {File} file - The image file to convert
 * @returns {Promise<string>} Base64 encoded string
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Save logo to localStorage
 * @param {string} base64Logo - Base64 encoded logo
 */
export const saveLogo = (base64Logo) => {
  try {
    localStorage.setItem('companyLogo', base64Logo);
    return true;
  } catch (err) {
    console.error('Error saving logo:', err);
    return false;
  }
};

/**
 * Get logo from localStorage
 * @returns {string|null} Base64 encoded logo or null
 */
export const getLogo = () => {
  try {
    return localStorage.getItem('companyLogo') || null;
  } catch (err) {
    console.error('Error retrieving logo:', err);
    return null;
  }
};

/**
 * Remove logo from localStorage
 */
export const removeLogo = () => {
  try {
    localStorage.removeItem('companyLogo');
    return true;
  } catch (err) {
    console.error('Error removing logo:', err);
    return false;
  }
};

/**
 * Handle logo file upload with validation
 * @param {File} file - The image file
 * @param {number} maxSizeMB - Maximum file size in MB (default: 5MB)
 * @returns {Promise<Object>} Result object with success status and data/error
 */
export const uploadLogoWithValidation = async (file, maxSizeMB = 5) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  // Validate file type
  if (!validImageTypes.includes(file.type)) {
    return {
      success: false,
      error: 'Invalid file type. Please upload JPG, PNG, GIF, or WebP image.'
    };
  }

  // Validate file size
  if (file.size > maxSizeBytes) {
    return {
      success: false,
      error: `File size exceeds ${maxSizeMB}MB limit. Please choose a smaller image.`
    };
  }

  try {
    const base64 = await fileToBase64(file);
    const saved = saveLogo(base64);
    
    if (saved) {
      return {
        success: true,
        data: base64,
        message: 'Logo uploaded successfully!'
      };
    } else {
      return {
        success: false,
        error: 'Failed to save logo. Please try again.'
      };
    }
  } catch (err) {
    return {
      success: false,
      error: `Error uploading logo: ${err.message}`
    };
  }
};
